import { useState, useEffect } from 'react';
import { FaBars, FaHome, FaUser, FaClipboardList, FaProjectDiagram, FaBuilding, FaCalendarAlt, FaUserFriends, FaHistory, FaCog } from 'react-icons/fa';
import { HoneycombBackground } from './Auth/HoneycombBackground';
import { LanguageSelector } from './LanguageSelector';
import { useAuth } from '../contexts/AuthContext';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export default function DynamicIslandMenu() {
  const { currentUser, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [businessLogo, setBusinessLogo] = useState<string>('https://firebasestorage.googleapis.com/v0/b/brix-v2.firebasestorage.app/o/Marca%2FRecurso%205.svg?alt=media&token=65d56a39-541f-4ec0-864f-fc2105cf9c54');
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser) {
      const fetchUserData = async () => {
        const db = getFirestore();
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          setUserData(data);
          if (data.language) {
            i18n.changeLanguage(data.language);
          }
          
          // Cargar logo del negocio si el usuario es employee o business con businessId
          if ((data.role === 'employee' || data.role === 'business') && data.businessId) {
            try {
              const businessRef = doc(db, 'businesses', data.businessId);
              const businessSnap = await getDoc(businessRef);
              if (businessSnap.exists()) {
                const businessData = businessSnap.data();
                if (businessData.logo) {
                  setBusinessLogo(businessData.logo);
                }
              }
            } catch (error) {
              console.error('Error fetching business logo:', error);
            }
          }
        }
      };
      fetchUserData();
    }
  }, [currentUser, i18n]);

  const handleCloseMenu = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsMenuOpen(false);
      setIsClosing(false);
    }, 600);
  };

  const handleMenuItemClick = (path: string) => {
    setIsClosing(true);
    setTimeout(() => {
      setIsMenuOpen(false);
      setIsClosing(false);
      navigate(path);
    }, 600);
  };

  const handleLogout = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsMenuOpen(false);
      setIsClosing(false);
      logout();
    }, 600);
  };

  return (
    <div className="fixed top-0 left-0 w-screen z-[3000]">
      {/* Botón flotante tipo Dynamic Island */}
      <button
        className={`absolute top-0 left-1/2 -translate-x-1/2 h-9 bg-none border-none p-0 cursor-pointer pointer-events-auto flex flex-col items-center justify-start z-[3100] transition-all duration-300 overflow-visible group ${isHovered ? 'w-40' : 'w-12'}`}
        onClick={() => setIsMenuOpen(true)}
        aria-label={t('menu.title')}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className={`w-full h-9 transition-all duration-300 ${isHovered ? 'pb-0' : 'pb-3'}`}>
          <svg width="100%" height="36" viewBox="0 0 120 18" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="block transition-all duration-300">
            <defs>
              <linearGradient id="island-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#B3122C">
                  {isHovered && <animate attributeName="offset" values="0;0.2;0" dur="3s" repeatCount="indefinite" />}
                </stop>
                <stop offset="100%" stopColor={isHovered ? "#D50B3F" : "#B3122C"}>
                  {isHovered && <animate attributeName="offset" values="1;0.8;1" dur="3s" repeatCount="indefinite" />}
                </stop>
              </linearGradient>
            </defs>
            <path d="M0,0 H120 C120,0 112,6 112,10 C112,14 100,18 80,18 C60,18 60,18 40,18 C20,18 8,14 8,10 C8,6 0,0 0,0 Z" fill={isHovered ? "url(#island-gradient)" : "#D50B3F"} />
          </svg>
        </div>
        <div className="absolute top-0 left-0 w-full h-7 flex items-center justify-center text-white font-montserrat text-base font-bold tracking-wide gap-2 transition-all duration-300">
          <span className="opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300 hidden group-hover:inline-block">{t('menu.title')}</span>
          <FaBars className="text-lg" />
        </div>
      </button>

      {/* Menú fullscreen tipo Dynamic Island */}
      {isMenuOpen && (
        <div className={`fixed top-0 left-0 w-screen h-screen z-[4000] flex flex-col items-end justify-start ${isClosing ? 'animate-slideUpMenu' : 'animate-slideDownMenu'} overflow-hidden`}>
          <HoneycombBackground />
          <div className="absolute inset-0 w-screen h-screen bg-black opacity-15 z-1 pointer-events-none" />
          {/* Botón cerrar */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
            <button
              className="w-32 h-9 bg-none border-none p-0 cursor-pointer flex items-center justify-center relative outline-none"
              onClick={handleCloseMenu}
              aria-label={t('menu.close')}
            >
              <svg viewBox="0 0 120 36" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="absolute top-0 left-0 w-32 h-9 z-1">
                <path d="M0,0 H120 C120,0 112,12 112,20 C112,28 100,36 80,36 C60,36 60,36 40,36 C20,36 8,28 8,20 C8,12 0,0 0,0 Z" fill="#383838" />
              </svg>
              <span className="relative z-2 text-white text-2xl font-bold flex items-center justify-center">×</span>
            </button>
          </div>
          <div className="w-screen h-screen flex flex-row relative z-2">
            {/* Logo en la esquina superior izquierda */}
            <div className="absolute top-8 left-8 z-10">
              <img 
                src={businessLogo} 
                alt="Logo" 
                className="h-16 w-auto object-contain drop-shadow-lg"
              />
            </div>
            
            {/* Columna izquierda */}
            <div className="h-screen flex-[0_0_40%] bg-white flex flex-col items-start justify-center px-[3vw] font-montserrat">
              {userData?.role === 'admin' && (
                <>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/admin-dashboard')}>{t('menu.admin.dashboard')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/admin/skills')}>{t('menu.admin.skills')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/admin/user-passwords')}>{t('menu.admin.userPasswords')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/admin/approval-process')}>{t('admin.approvalProcess.title')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/admin/certification-migration')}>🔄 Migración de Certificaciones</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/admin/business-management')}>{t('admin.businessManagement.title', 'Gestión de Negocios Pendientes')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/admin/site-settings')}>{t('menu.settings', 'Configuración')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/admin/worker-removal-history')}>📝 Historial de Workers Removidos</button>
                </>
              )}
              {userData?.role === 'business' && (
                <>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/business/dashboard')}><FaHome />{t('menu.dashboard')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/business-agenda')}><FaCalendarAlt />{t('menu.businessAgenda', 'Agenda')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/quotations')}><FaClipboardList />{t('menu.quotations')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/projects')}><FaProjectDiagram />{t('menu.projects')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/business/clients')}><FaUserFriends />{t('menu.business.clients', 'Clientes')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/business/company')}><FaBuilding />{t('menu.myBusiness')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/business/profile')}><FaUser />{t('menu.profile')}</button>
                </>
              )}
              {userData?.role === 'worker' && (
                <>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/worker-dashboard')}>{t('menu.worker.dashboard')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/balance')}>{t('menu.worker.balance')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/worker-projects')}><FaProjectDiagram />{t('workerProjects.title', 'Mis Proyectos')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/worker-agenda')}><FaCalendarAlt />{t('menu.workerAgenda', 'Mi Agenda')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/worker-job-history')}><FaHistory />{t('menu.jobHistory', 'Historial de Trabajos')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/my-certifications')}>{t('workerDashboard.certifications')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/profile')}><FaUser />{t('menu.profile')}</button>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/worker-settings')}><FaCog />{t('menu.settings', 'Configuración')}</button>
                </>
              )}
              {userData?.role === 'employee' && (
                <>
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/employee-dashboard')}><FaHome />{t('menu.dashboard')}</button>
                  {userData?.businessId && userData?.status === 'active' && (
                    <>
                      <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/business-agenda')}><FaCalendarAlt />{t('menu.businessAgenda', 'Agenda')}</button>
                      <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/quotations')}><FaClipboardList />{t('menu.quotations')}</button>
                      <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/projects')}><FaProjectDiagram />{t('menu.projects')}</button>
                      <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/business/clients')}><FaUserFriends />{t('menu.business.clients', 'Clientes')}</button>
                    </>
                  )}
                  <button className="flex items-center gap-2 py-3 w-full text-black text-base font-medium transition-colors hover:text-gray-600" onClick={() => handleMenuItemClick('/profile')}><FaUser />{t('menu.profile')}</button>
                </>
              )}
            </div>
            {/* Columna central: selector de idioma */}
            <div className="h-screen flex-[0_0_40%] flex flex-col items-center justify-start">
              <LanguageSelector />
            </div>
            {/* Columna derecha vacía */}
            <div className="h-screen flex-[0_0_20%] flex flex-col items-end justify-start pt-20 pr-[2vw]" />
          </div>
          {/* Botón logout flotante */}
          <button
            className="absolute left-10 bottom-10 bg-white text-[#D50B3F] border-none rounded-2xl py-3 px-8 font-montserrat text-lg font-bold tracking-wide shadow-lg cursor-pointer z-10 transition-all hover:bg-[#D50B3F] hover:text-white hover:shadow-xl"
            onClick={handleLogout}
          >
            {t('logout')}
          </button>
        </div>
      )}
    </div>
  );
} 